/**
 * Agent id of the "Agent Builder" custom agent built on the Rain runtime. The
 * Builder converses with a user to construct a *target* agent, calling
 * `custom_remote` tools that this client fulfills against the target agent's
 * config thread (see `useBuilderToolFulfillment`). Defined server-side in the
 * `rain` repo under this fixed id.
 */
export const RAIN_BUILDER_AGENT_ID = 'smith';

/**
 * Builder tools, split by fulfillment path:
 *
 *  - SERVER-SIDE (Rain's own grpc sources): most of the work. The identity
 *    writes and config reads go to `rain-memory` (MemoryService) — all writes
 *    via `set_memory_config`, where persona / instructions take a `content`
 *    config key and tone & style take `tone` + `style`. The tool-source and
 *    per-tool writes go to the same source's ToolCatalogService tools, and
 *    `search_integrations` goes to `bdk-library`. Rain dials these itself; this
 *    client does NOT fulfill them. They still render in chat as progress, and
 *    Rain returns the tool result. See agent-smith/smith.k8s.yaml.
 *  - CLIENT-SIDE (`custom_remote`, fulfilled in `useBuilderToolFulfillment`):
 *    only what genuinely needs the browser — the knowledge-file registry, plus
 *    `add_integration` / `attach_file`, which are resolved by picker cards
 *    rather than auto-fulfilled. `add_integration` returns the connection id the
 *    user picked; attaching it to the agent is a server-side `add_tool_source`.
 *
 * Names MUST match the server-side tool definitions in smith.k8s.yaml exactly.
 * This mirrors what the Agent Preview pane can edit manually, so the two edit
 * paths (chat vs. pane) stay at parity.
 */
export enum EBuilderTool {
  // Server-side (Rain MemoryService / ToolCatalogService grpc source) — see
  // smith.k8s.yaml. Writes (one tool for every identity block: persona and
  // system_instructions write `content`, tone_style writes `tone`/`style` —
  // ReplaceMemoryContent can't set persona/instructions, since both kinds
  // hardcode `set_content` to NotMutable):
  SetMemoryConfig = 'set_memory_config',
  // Tool-source and per-tool writes (ToolCatalogService). enable/disable take
  // (sourceId, toolId) — the model reads list_agent_tools for those ids:
  ListToolSourceKinds = 'list_tool_source_kinds',
  AddToolSource = 'add_tool_source',
  RemoveToolSource = 'remove_tool_source',
  EnableTool = 'enable_tool',
  DisableTool = 'disable_tool',
  // Bulk enable — (sourceId, toolIds[]). smith.k8s.yaml tells the model to
  // prefer this over repeated `enable_tool` when switching on a whole source,
  // and an automation is always a GROUP of tools (the automation itself plus
  // its `__get_run_*` companions), so this is the call that lands for
  // automations. It must be listed here for the same reason as its singular
  // sibling: absent from the enum it is neither invalidated nor rendered.
  EnableTools = 'enable_tools',
  // Reads (stateless config lookups; Rain fulfills these too):
  ListMemoryBlocks = 'list_memory_blocks',
  GetMemoryBlock = 'get_memory_block',
  ListAgentTools = 'list_agent_tools',
  ListAgentToolSources = 'list_agent_tool_sources',
  // Integration catalog search (bdk LibraryService.SearchBooks — semantic
  // search over the global catalog, so it needs no tenant scope):
  SearchIntegrations = 'search_integrations',
  // Client-side (`custom_remote`): picker cards + the knowledge-file registry,
  // fulfilled in `useBuilderToolFulfillment` / the card resolvers.
  AddIntegration = 'add_integration',
  AttachFile = 'attach_file',
  RemoveFile = 'remove_file',
  ListFiles = 'list_files',
  // Resolves through the same connection-picker card as `add_integration`.
  RequestConnection = 'request_connection',
  // The build canvas (smith v30), fulfilled by `useCanvasTools`.
  UpdateBuildPlan = 'update_build_plan',
  CreateAutomation = 'create_automation',
  PublishAutomation = 'publish_automation',
  AddEmailTrigger = 'add_email_trigger',
  CreateCollection = 'create_collection',
  ListContextGraphs = 'list_context_graphs',
  AttachContextGraph = 'attach_context_graph',
  CreateContextGraph = 'create_context_graph',
}

/**
 * The tools fulfilled SERVER-SIDE by Rain's own grpc sources (MemoryService for
 * identity, ToolCatalogService for tool sources and per-tool toggles) — not by
 * this client. Kept as a named set so the visible-tool logic can surface their
 * progress cards without the client trying to resolve them.
 */
export const BUILDER_SERVER_FULFILLED_TOOLS: ReadonlySet<string> = new Set([
  EBuilderTool.SetMemoryConfig,
  EBuilderTool.ListMemoryBlocks,
  EBuilderTool.GetMemoryBlock,
  EBuilderTool.ListAgentTools,
  EBuilderTool.ListAgentToolSources,
  EBuilderTool.ListToolSourceKinds,
  EBuilderTool.AddToolSource,
  EBuilderTool.RemoveToolSource,
  EBuilderTool.EnableTool,
  EBuilderTool.EnableTools,
  EBuilderTool.DisableTool,
  EBuilderTool.SearchIntegrations,
]);

/**
 * The Builder tools this client auto-executes (returns a result for). Excludes
 * everything Rain fulfills server-side, and `add_integration` / `attach_file`,
 * which are resolved by picker cards (connection picker / file picker) exactly
 * like `request_connection`.
 */
export const BUILDER_AUTO_FULFILLED_TOOLS: ReadonlySet<string> = new Set([
  EBuilderTool.RemoveFile,
  EBuilderTool.ListFiles,
  EBuilderTool.UpdateBuildPlan,
  EBuilderTool.CreateAutomation,
  EBuilderTool.PublishAutomation,
  EBuilderTool.AddEmailTrigger,
  EBuilderTool.CreateCollection,
  EBuilderTool.ListContextGraphs,
  EBuilderTool.AttachContextGraph,
  EBuilderTool.CreateContextGraph,
]);

/**
 * The required argument(s) that mean a Builder tool's arguments have finished
 * streaming and the call is ready to fulfill. Mirrors how the
 * `request_connection` card gates on `appName && appVersion` before acting.
 */
export const BUILDER_TOOL_REQUIRED_ARGS: Record<string, readonly string[]> = {
  // The server-fulfilled tools (see BUILDER_SERVER_FULFILLED_TOOLS) are absent
  // on purpose: Rain resolves them, so this client never gates them — it only
  // renders their progress (every Builder tool is in the visible set).
  // remove_file drops a named file; list_files takes no args (ready as soon as
  // it's called). attach_file is a card (not auto-fulfilled), so it's absent.
  [EBuilderTool.RemoveFile]: ['fileName'],
  [EBuilderTool.ListFiles]: [],
  [EBuilderTool.UpdateBuildPlan]: ['plan_json'],
  [EBuilderTool.CreateAutomation]: ['name', 'description'],
  [EBuilderTool.PublishAutomation]: ['automation_id'],
  [EBuilderTool.AddEmailTrigger]: ['prompt'],
  [EBuilderTool.CreateCollection]: ['name', 'fields_json'],
  [EBuilderTool.ListContextGraphs]: [],
  [EBuilderTool.AttachContextGraph]: ['graph_id'],
  [EBuilderTool.CreateContextGraph]: ['name', 'brief'],
};
